/**
 * GeoJSON Feature/FeatureCollection builders.
 *
 * Mapbox GL JS works natively with GeoJSON sources. These utilities
 * convert domain types to GeoJSON features for use in map sources.
 */

import type { GeoCoord, MapField, MapTree, MapFarmer, MapCohort, MapWarehouse } from '../types/mapTypes';

// ─── GeoJSON primitives ─────────────────────────────────────────────

export interface GeoJsonFeature<G = GeoJsonGeometry, P = Record<string, unknown>> {
  type: 'Feature';
  id: string | number;
  geometry: G;
  properties: P;
}

export type GeoJsonGeometry =
  | GeoJsonPoint
  | GeoJsonPolygon
  | GeoJsonMultiPolygon;

export interface GeoJsonPoint {
  type: 'Point';
  coordinates: GeoCoord;
}

export interface GeoJsonPolygon {
  type: 'Polygon';
  coordinates: GeoCoord[][];
}

export interface GeoJsonMultiPolygon {
  type: 'MultiPolygon';
  coordinates: GeoCoord[][][];
}

export interface FeatureCollection<G = GeoJsonGeometry, P = Record<string, unknown>> {
  type: 'FeatureCollection';
  features: GeoJsonFeature<G, P>[];
}

// ─── Builders ───────────────────────────────────────────────────────

export function pointFeature(id: string | number, coord: GeoCoord, props: Record<string, unknown> = {}): GeoJsonFeature {
  return {
    type: 'Feature',
    id,
    geometry: { type: 'Point', coordinates: coord },
    properties: props,
  };
}

export function polygonFeature(id: string | number, boundary: GeoCoord[], props: Record<string, unknown> = {}): GeoJsonFeature {
  if (boundary.length < 3) return emptyPolygonFeature(id, props);
  const first = boundary[0];
  const last = boundary[boundary.length - 1];
  const ring = first[0] === last[0] && first[1] === last[1]
    ? boundary
    : [...boundary, first];
  // Mapbox uses [lng, lat] which matches our GeoCoord
  return {
    type: 'Feature',
    id,
    geometry: {
      type: 'Polygon',
      coordinates: [ring],
    },
    properties: props,
  };
}

function emptyPolygonFeature(id: string | number, props: Record<string, unknown> = {}): GeoJsonFeature {
  return {
    type: 'Feature',
    id,
    geometry: {
      type: 'Polygon',
      coordinates: [[[0, 0], [0, 0], [0, 0], [0, 0]]],
    },
    properties: { ...props, _empty: true },
  };
}

export function featureCollection<G = GeoJsonGeometry, P = Record<string, unknown>>(
  features: GeoJsonFeature<G, P>[],
): FeatureCollection<G, P> {
  return { type: 'FeatureCollection', features };
}

// ─── Domain → GeoJSON ──────────────────────────────────────────────

export function fieldToFeature(field: MapField): GeoJsonFeature {
  return polygonFeature(field.id, field.boundary, {
    _type: 'field',
    id: field.id,
    name: field.name,
    plotCode: field.plotCode ?? '',
    cropType: field.cropType ?? '',
    status: field.status ?? '',
    areaHectares: field.areaHectares ?? 0,
    farmerName: field.primaryFarmerName ?? field.farmerName ?? '',
    farmerCount: field.farmerCount,
    treeCount: field.treeCount,
    mappedTreeCount: field.mappedTreeCount,
    activityCount: field.activityCount,
    cohortName: field.cohortName ?? '',
  });
}

export function treeToFeature(tree: MapTree): GeoJsonFeature {
  return pointFeature(tree.id, tree.position, {
    _type: 'tree',
    id: tree.id,
    treeCode: tree.treeCode,
    cropType: tree.cropType,
    variety: tree.variety ?? '',
    healthStatus: tree.healthStatus,
    treeStatus: tree.treeStatus,
    farmerName: tree.farmerName ?? '',
    cohortName: tree.cohortName ?? '',
    openIssueCount: tree.openIssueCount,
    latestIssuePhoto: tree.latestIssuePhoto ?? '',
  });
}

export function farmerToFeature(farmer: MapFarmer): GeoJsonFeature {
  return polygonFeature(farmer.id, farmer.boundary, {
    _type: 'farmer',
    id: farmer.id,
    name: farmer.name,
    householdType: farmer.householdType,
    cohortName: farmer.cohortName ?? '',
    areaHectares: farmer.areaHectares ?? 0,
  });
}

export function cohortToFeature(cohort: MapCohort): GeoJsonFeature {
  return polygonFeature(cohort.id, cohort.boundary, {
    _type: 'cohort',
    id: cohort.id,
    name: cohort.name,
    croppingSystem: cohort.croppingSystem ?? '',
    boundaryColor: cohort.boundaryColor ?? '#10B981',
    farmerCount: cohort.farmerCount,
    totalAreaHectares: cohort.totalAreaHectares,
  });
}

export function warehouseToFeature(warehouse: MapWarehouse): GeoJsonFeature {
  return pointFeature(warehouse.id, warehouse.position, {
    _type: 'warehouse',
    id: warehouse.id,
    name: warehouse.name,
    type: warehouse.type,
    locationName: warehouse.locationName,
    capacityKg: warehouse.capacityKg,
    currentUsageKg: warehouse.currentUsageKg,
    status: warehouse.status,
    usagePercentage: warehouse.usagePercentage,
    temperatureCelsius: warehouse.temperatureCelsius,
  });
}

// ─── GeoJSON parsing (for admin boundaries, etc.) ──────────────────

export function parseGeoJsonMaybe(input: unknown): FeatureCollection | null {
  if (!input || typeof input !== 'object') return null;
  const obj = input as Record<string, unknown>;
  if (obj.type === 'FeatureCollection' && Array.isArray(obj.features)) {
    return obj as unknown as FeatureCollection;
  }
  if (obj.type === 'Feature') {
    return featureCollection([obj as unknown as GeoJsonFeature]);
  }
  return null;
}
